/**
 * Public, unauthenticated acceptance page of the training center's general
 * term (`/terms/:token`), reached from a link the center hands out or from
 * the student's plan in the app. Journey: read the full term → "Aceitar"
 * sends an OTP to the signer's registered phone (the student, or their
 * guardian) → type the 6-digit code → signed, receipt shown.
 *
 * Evidence contract with the backend: the page echoes back the
 * `contentSha256` it displayed; if the text changed meanwhile the accept
 * returns 409 `contract_terms_changed` and the page reloads the new text —
 * what is signed is always exactly what was on screen. `outdated` means the
 * center's rules changed since the last acceptance: sign the new version.
 * `?a=<acceptanceId>` opens one stored acceptance as a receipt, and a legacy
 * per-contract link still pending is swapped for the student's general link.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import {
  Body,
  Button,
  ButtonVariant,
  Caption,
  H1,
  H2,
  Input,
  MarkdownContent
} from '@meiuca/ui-components'
import { Check, ShieldCheck } from 'lucide-react'
import { MeiucaHttpError, NotFoundError, RateLimitedError } from '@meiuca/service'
import { formatDateTimeForUI } from '@meiuca/web-foundation'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { toastManager } from '@/lib/toastManager'
import {
  contractTermsService,
  type PublicContractTermsPayload
} from '@/services/contractTermsService'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; payload: PublicContractTermsPayload }

/** Sub-flow while the term is pending. */
type SignStep =
  | { kind: 'reading' }
  | { kind: 'otp'; maskedPhone: string; resendAt: number }

export default function AcceptContractTermsPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const acceptanceId = searchParams.get('a')
  const navigate = useNavigate()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [step, setStep] = useState<SignStep>({ kind: 'reading' })

  useEffect(() => {
    document.title = 'Termo de aceite · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.CONTRACT_TERMS)
  }, [])

  const load = useCallback(() => {
    if (!token) {
      setState({ kind: 'not-found' })
      return
    }
    setState({ kind: 'loading' })
    setStep({ kind: 'reading' })
    contractTermsService
      .getByToken(token, acceptanceId)
      .then((payload) => {
        if (payload.redirectToken) {
          // Legacy per-contract link still pending: the student's general term
          // replaces it.
          navigate(`/terms/${payload.redirectToken}`, { replace: true })
          return
        }
        setState({ kind: 'ok', payload })
      })
      .catch((err) => {
        if (err instanceof NotFoundError) {
          setState({ kind: 'not-found' })
        } else {
          setState({ kind: 'error', message: 'Não conseguimos carregar o termo agora.' })
        }
      })
  }, [token, acceptanceId, navigate])

  useEffect(() => {
    load()
  }, [load])

  return (
    <main className="min-h-screen bg-background-primary flex flex-col">
      <PageHeader />

      <section className="flex-1 flex items-start justify-center px-4 py-8 md:py-14">
        <div className="w-full max-w-2xl space-y-4">
          {state.kind === 'loading' && <LoadingState />}
          {state.kind === 'not-found' && <NotFoundState />}
          {state.kind === 'error' && <ErrorState message={state.message} onRetry={load} />}
          {state.kind === 'ok' && state.payload.status === 'accepted' && (
            <AcceptedView payload={state.payload} />
          )}
          {state.kind === 'ok' && state.payload.status !== 'accepted' && token && (
            <PendingView
              token={token}
              payload={state.payload}
              step={step}
              onStepChange={setStep}
              onReload={load}
            />
          )}
        </div>
      </section>

      <PageFooter />
    </main>
  )
}

function PageHeader() {
  return (
    <header className="border-b border-gray-200/40 bg-background-white/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 md:h-20">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="Meiuca" className="w-7 h-7" width={28} height={28} />
            <span className="text-lg md:text-xl font-bold text-text-primary">Meiuca</span>
          </div>
        </div>
      </div>
    </header>
  )
}

function PageFooter() {
  return (
    <footer className="border-t border-gray-200/40 bg-background-secondary py-6 px-5">
      <div className="max-w-2xl mx-auto text-center">
        <Caption className="text-text-tertiary">
          Aceite eletrônico registrado pela Meiuca — art. 10, §2º, MP 2.200-2/2001.
        </Caption>
      </div>
    </footer>
  )
}

function LoadingState() {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm">
      <Body className="text-text-secondary">Carregando termo…</Body>
    </div>
  )
}

function NotFoundState() {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm">
      <H2 className="mb-2">Termo não encontrado</H2>
      <Body className="text-text-secondary">
        O link pode estar incorreto ou indisponível. Confira a mensagem original ou fale com
        quem enviou o termo.
      </Body>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm space-y-4">
      <H2>Algo deu errado</H2>
      <Body className="text-text-secondary">{message}</Body>
      <Button variant={ButtonVariant.SECONDARY} onClick={onRetry}>
        Tentar de novo
      </Button>
    </div>
  )
}

function AcceptedView({ payload }: { payload: PublicContractTermsPayload }) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-background-white p-6 md:p-8 shadow-sm space-y-4">
        <div className="flex items-start gap-3 rounded-xl bg-green-50 p-4 text-green-900">
          <Check className="mt-0.5 flex-shrink-0" size={20} />
          <div>
            <Body className="font-semibold">Termo aceito</Body>
            {payload.acceptedAt && (
              <Caption className="text-green-800">
                {formatDateTimeForUI(payload.acceptedAt)} · {payload.signerDisplayName}
              </Caption>
            )}
          </div>
        </div>
        <Caption className="text-text-tertiary break-all">
          Código de verificação do documento (SHA-256): {payload.contentSha256}
        </Caption>
      </div>

      <TermCard payload={payload} />
    </div>
  )
}

function PendingView({
  token,
  payload,
  step,
  onStepChange,
  onReload
}: {
  token: string
  payload: PublicContractTermsPayload
  step: SignStep
  onStepChange: (step: SignStep) => void
  onReload: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-background-white p-6 md:p-8 shadow-sm space-y-2">
        <H1 className="text-2xl md:text-3xl">Termo de aceite</H1>
        <Body className="text-text-secondary">
          {payload.centerName} enviou este termo para <strong>{payload.signerDisplayName}</strong>.
          Leia o documento até o final e confirme o aceite com o código enviado ao seu WhatsApp.
        </Body>
        {payload.status === 'outdated' && (
          <div className="rounded-xl bg-feedback-warning/10 border border-feedback-warning/40 p-4">
            <Body className="text-sm text-text-primary m-0">
              {payload.issuerKind === 'arena' ? 'As regras da arena mudaram' : 'As regras do centro mudaram'}
              {payload.previousAcceptedAt
                ? ` desde o último aceite, em ${formatDateTimeForUI(payload.previousAcceptedAt)}`
                : ' desde o último aceite'}
              . Leia a versão atualizada e confirme novamente.
            </Body>
          </div>
        )}
      </div>

      <TermCard payload={payload} />

      <div className="rounded-2xl bg-background-white p-6 md:p-8 shadow-sm">
        {step.kind === 'reading' && (
          <ReadingActions
            token={token}
            termStatus={payload.status}
            maskedPhone={payload.maskedPhone}
            issuerKind={payload.issuerKind}
            onOtpSent={(maskedPhone, resendAfter) =>
              onStepChange({ kind: 'otp', maskedPhone, resendAt: Date.now() + resendAfter * 1000 })
            }
            onReload={onReload}
          />
        )}
        {step.kind === 'otp' && (
          <OtpStep
            token={token}
            termStatus={payload.status}
            contentSha256={payload.contentSha256}
            maskedPhone={step.maskedPhone}
            resendAt={step.resendAt}
            onResent={(maskedPhone, resendAfter) =>
              onStepChange({ kind: 'otp', maskedPhone, resendAt: Date.now() + resendAfter * 1000 })
            }
            onReload={onReload}
          />
        )}
      </div>
    </div>
  )
}

function TermCard({ payload }: { payload: PublicContractTermsPayload }) {
  return (
    <div className="rounded-2xl bg-background-white p-6 md:p-8 shadow-sm">
      <MarkdownContent className="text-text-primary">{payload.content}</MarkdownContent>
    </div>
  )
}

function ReadingActions({
  token,
  termStatus,
  maskedPhone,
  issuerKind,
  onOtpSent,
  onReload
}: {
  token: string
  termStatus: PublicContractTermsPayload['status']
  maskedPhone: string | null
  issuerKind?: PublicContractTermsPayload['issuerKind']
  onOtpSent: (maskedPhone: string, resendAfter: number) => void
  onReload: () => void
}) {
  const [isSending, setIsSending] = useState(false)

  if (!maskedPhone) {
    return (
      <Body className="text-text-secondary">
        Não encontramos um telefone cadastrado para confirmar o aceite.{' '}
        {issuerKind === 'arena'
          ? 'Fale com a arena para cadastrar o telefone de contato.'
          : 'Fale com o centro de treinamento para completar seu cadastro.'}
      </Body>
    )
  }

  const sendOtp = async () => {
    setIsSending(true)
    try {
      const result = await contractTermsService.sendOtp(token)
      analyticsService.track(ANALYTICS_EVENTS.CONTRACT_TERMS_OTP_SENT, { status: termStatus })
      onOtpSent(result.maskedPhone, result.resendAfter)
    } catch (err) {
      handleFlowError(err, onReload)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 text-text-secondary">
        <ShieldCheck className="mt-0.5 flex-shrink-0" size={18} />
        <Caption>
          Para confirmar, enviaremos um código de 6 dígitos ao WhatsApp {maskedPhone}.
        </Caption>
      </div>
      <Button
        variant={ButtonVariant.PRIMARY}
        className="w-full"
        disabled={isSending}
        onClick={sendOtp}
      >
        {isSending ? 'Enviando código…' : 'Aceitar termo'}
      </Button>
    </div>
  )
}

function OtpStep({
  token,
  termStatus,
  contentSha256,
  maskedPhone,
  resendAt,
  onResent,
  onReload
}: {
  token: string
  termStatus: PublicContractTermsPayload['status']
  contentSha256: string
  maskedPhone: string
  resendAt: number
  onResent: (maskedPhone: string, resendAfter: number) => void
  onReload: () => void
}) {
  // Verification token and OTP live in memory only — never in localStorage.
  const [otpCode, setOtpCode] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [resendIn, setResendIn] = useState(() => secondsUntil(resendAt))
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    setResendIn(secondsUntil(resendAt))
    intervalRef.current = setInterval(() => {
      setResendIn(secondsUntil(resendAt))
    }, 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [resendAt])

  const confirm = async () => {
    setIsConfirming(true)
    try {
      const verificationToken = await contractTermsService.verifyOtp(token, otpCode)
      analyticsService.track(ANALYTICS_EVENTS.CONTRACT_TERMS_OTP_VERIFIED, { status: termStatus })
      await contractTermsService.accept(token, verificationToken, contentSha256)
      analyticsService.track(ANALYTICS_EVENTS.CONTRACT_TERMS_ACCEPTED, { status: termStatus })
      // Reload: the accepted view must show the STORED record.
      onReload()
    } catch (err) {
      if (err instanceof MeiucaHttpError && err.code === 'contract_terms_otp_invalid') {
        toastManager.error('Código inválido ou expirado. Confira e tente de novo.')
        setOtpCode('')
      } else {
        handleFlowError(err, onReload)
      }
    } finally {
      setIsConfirming(false)
    }
  }

  const resend = async () => {
    setIsResending(true)
    try {
      const result = await contractTermsService.sendOtp(token)
      analyticsService.track(ANALYTICS_EVENTS.CONTRACT_TERMS_OTP_SENT, { status: termStatus })
      toastManager.success('Novo código enviado.')
      onResent(result.maskedPhone, result.resendAfter)
    } catch (err) {
      if (err instanceof RateLimitedError) {
        toastManager.error('Aguarde alguns segundos para reenviar.')
      } else {
        handleFlowError(err, onReload)
      }
    } finally {
      setIsResending(false)
    }
  }

  const canConfirm = otpCode.length === 6 && !isConfirming

  return (
    <div className="space-y-4">
      <Body className="text-text-secondary">
        Digite o código de 6 dígitos enviado ao WhatsApp {maskedPhone}.
      </Body>
      <Input
        value={otpCode}
        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        className="text-center text-2xl tracking-[0.5em]"
      />
      <Button
        variant={ButtonVariant.PRIMARY}
        className="w-full"
        disabled={!canConfirm}
        onClick={confirm}
      >
        {isConfirming ? 'Confirmando…' : 'Confirmar aceite'}
      </Button>
      <div className="text-center">
        {resendIn > 0 ? (
          <Caption className="text-text-tertiary">Reenviar código em {resendIn}s</Caption>
        ) : (
          <button
            type="button"
            className="text-sm text-text-secondary underline disabled:opacity-50"
            disabled={isResending}
            onClick={resend}
          >
            {isResending ? 'Reenviando…' : 'Reenviar código'}
          </button>
        )}
      </div>
    </div>
  )
}

function secondsUntil(timestamp: number): number {
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000))
}

/**
 * Shared handling for flow-breaking backend answers: states where the page's
 * copy of the term (or the whole flow) is stale and the honest move is to
 * reload and let the fresh state drive the UI.
 */
function handleFlowError(err: unknown, onReload: () => void) {
  if (err instanceof MeiucaHttpError) {
    switch (err.code) {
      case 'contract_terms_changed':
        toastManager.error('O termo foi atualizado. Releia o documento e aceite novamente.')
        onReload()
        return
      case 'contract_terms_already_accepted':
        onReload()
        return
      case 'contract_terms_verification_invalid':
        toastManager.error('A verificação expirou. Solicite um novo código.')
        onReload()
        return
      case 'contract_terms_payer_phone_missing':
        toastManager.error('Não há telefone cadastrado. Fale com quem enviou o termo.')
        onReload()
        return
    }
  }
  toastManager.error('Algo deu errado. Tente novamente em instantes.')
}
